/**
 * Iter 102 — Cinematic 3D dice roll modal.
 *
 * Two CSS-3D cubes spinning side-by-side (tens digit + units digit) form a
 * percentile (1-100). The animation starts IMMEDIATELY when the player
 * picks a risky choice — BEFORE the AI generates the new story page — so
 * the player feels the suspense of the roll while the backend works.
 *
 * Lifecycle:
 *   1. Player clicks a risky choice → parent sets `roll = { pending: true,
 *      label, success_probability, ... }` and starts the API call.
 *   2. Modal opens, dice begin tumbling (random faces every 80ms).
 *   3. API responds with the real `risk_roll` → parent updates `roll` with
 *      `pending: false, percentile, tens, units, result, ...`.
 *   4. Dice continue tumbling until BOTH:
 *      - min spin duration (1.6s) is elapsed
 *      - roll.pending is false
 *      Then they lock onto the target faces with a soft "land" animation.
 *   5. Result banner appears 600ms later. "Continue" button unlocks.
 *
 * Props:
 *   roll: {
 *     pending: bool,          // true while the AI is still generating
 *     label: string,
 *     fail_probability?: number,
 *     success_probability?: number,
 *     percentile?: number,    // 1..100
 *     tens?: number,          // 0..10 (face shown on the tens die)
 *     units?: number,         // 0..9  (face shown on the units die)
 *     pct_threshold?: number, // 1..100
 *     result?: "success" | "failure",
 *   }
 *   lang: "it" | "en"
 *   onClose: () => void
 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Skull, ArrowRight, Loader2 } from "lucide-react";

const MIN_SPIN_MS = 1600;
const FRAME_MS = 80;
const LAND_GAP_MS = 600;

const STR = {
  it: {
    title: "Lancio dei Dadi",
    threshold_prefix: "Soglia per riuscire",
    success_chance: "Probabilità di riuscita",
    success: "RIUSCITO!",
    failure: "FALLITO...",
    continue: "Continua",
    rolling: "Il fato si sta scrivendo...",
    landed_lo: "Hai mancato la soglia",
    landed_hi: "Hai superato la soglia",
    your_roll: "Il tuo lancio",
  },
  en: {
    title: "Dice Roll",
    threshold_prefix: "Threshold to succeed",
    success_chance: "Chance of success",
    success: "SUCCESS!",
    failure: "FAILURE...",
    continue: "Continue",
    rolling: "Fate is being written...",
    landed_lo: "Below the threshold",
    landed_hi: "Above the threshold",
    your_roll: "Your roll",
  },
};

// ---- 3D dice component ----------------------------------------------
// A 6-faced cube where each face shows a digit. While `spinning`, the
// cube rotates with rapid random rotations + the face value changes.
// When spinning ends, it locks on `value` with a soft transform.
function DiceCube({ value, spinning, ariaLabel }) {
  // Random face content during spin (so the visible face is never empty)
  const [tumbleDigit, setTumbleDigit] = useState(0);
  // Random rotation while spinning
  const [rot, setRot] = useState({ x: 0, y: 0, z: 0 });
  const intervalRef = useRef(null);

  useEffect(() => {
    if (spinning) {
      intervalRef.current = setInterval(() => {
        setTumbleDigit(Math.floor(Math.random() * 10));
        setRot({
          x: Math.floor(Math.random() * 6) * 90 + (Math.random() * 60 - 30),
          y: Math.floor(Math.random() * 6) * 90 + (Math.random() * 60 - 30),
          z: (Math.random() * 30 - 15),
        });
      }, FRAME_MS);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      // Settle on a final straight-on rotation showing the front face
      setRot({ x: 0, y: 0, z: 0 });
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [spinning]);

  const displayDigit = spinning ? tumbleDigit : (value ?? 0);
  // Pre-compute opposite face & lateral faces so the cube looks solid
  // when partially rotated during the spin phase.
  const faces = [
    { transform: "rotateY(0deg) translateZ(40px)" },          // front
    { transform: "rotateY(90deg) translateZ(40px)" },          // right
    { transform: "rotateY(180deg) translateZ(40px)" },         // back
    { transform: "rotateY(-90deg) translateZ(40px)" },         // left
    { transform: "rotateX(90deg) translateZ(40px)" },          // top
    { transform: "rotateX(-90deg) translateZ(40px)" },         // bottom
  ];

  return (
    <div
      className="dice-cube-wrap"
      style={{ width: 80, height: 80, perspective: 300 }}
      aria-label={ariaLabel}
    >
      <div
        className="dice-cube"
        style={{
          position: "relative",
          width: 80,
          height: 80,
          transformStyle: "preserve-3d",
          transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg) rotateZ(${rot.z}deg)`,
          transition: spinning
            ? `transform ${FRAME_MS}ms linear`
            : "transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        {faces.map((f, i) => (
          <div
            key={i}
            className="dice-face"
            style={{
              position: "absolute",
              width: 80,
              height: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: spinning
                ? "linear-gradient(135deg, #1a1a1d 0%, #2d3748 60%, #475569 100%)"
                : "linear-gradient(135deg, #2c1810 0%, #5c3a1e 50%, #8a5a2b 100%)",
              border: `2px solid ${spinning ? "rgba(207,181,59,0.4)" : "#cfb53b"}`,
              borderRadius: 8,
              boxShadow: spinning
                ? "0 0 12px rgba(207,181,59,0.3)"
                : "inset 0 0 16px rgba(0,0,0,0.5), 0 0 24px rgba(207,181,59,0.4)",
              fontFamily: '"Cinzel", "Cormorant Garamond", serif',
              fontSize: 42,
              color: "#fef3c7",
              textShadow: "0 2px 4px rgba(0,0,0,0.8)",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              ...f,
            }}
          >
            {displayDigit}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DiceRollModal({ roll, lang = "it", onClose }) {
  const isIt = (lang || "it").startsWith("it");
  const STR_T = isIt ? STR.it : STR.en;

  const targetTens = roll?.tens;
  const targetUnits = roll?.units;
  const percentile = roll?.percentile;
  const threshold = roll?.pct_threshold ?? 50;
  const isSuccess = roll?.result === "success";
  const successPct = Math.round(
    ((roll?.success_probability) ?? (1 - (roll?.fail_probability ?? 0.5))) * 100
  );

  // phase: "spinning" (dice still tumbling) → "landed" (dice settled) → "revealed" (banner shown)
  const [phase, setPhase] = useState("spinning");
  const [tookTooLong, setTookTooLong] = useState(false);

  useEffect(() => {
    if (!roll?.pending) {
      setTookTooLong(false);
      return undefined;
    }
    const t = setTimeout(() => {
      setTookTooLong(true);
    }, 15000); // 15 seconds safety timeout
    return () => clearTimeout(t);
  }, [roll?.pending]);

  const startTsRef = useRef(Date.now());
  useEffect(() => {
    // We exit "spinning" only when BOTH conditions hold:
    //   1. response is in (roll.pending === false)
    //   2. min spin duration elapsed
    // Re-check whenever roll.pending flips.
    if (roll?.pending) return undefined;
    const elapsed = Date.now() - startTsRef.current;
    const remaining = Math.max(0, MIN_SPIN_MS - elapsed);
    const t1 = setTimeout(() => setPhase("landed"), remaining);
    const t2 = setTimeout(() => setPhase("revealed"), remaining + LAND_GAP_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [roll?.pending]);

  const isSpinning = phase === "spinning";

  // Color/glow style for the result block (computed AFTER landing).
  const resultBoxColor = isSuccess ? "#facc15" : "#ef4444";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-in fade-in duration-300"
      data-testid="dice-roll-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-w-md w-full bg-[#1a1a1d] border border-[#cfb53b]/40 rounded-lg p-6 sm:p-8 shadow-2xl">
        {/* Header */}
        <div className="text-center mb-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#94a3b8] font-heading mb-1">
            {STR_T.title}
          </p>
          <h2 className="font-heading text-xl sm:text-2xl text-[#cfb53b]" data-testid="dice-roll-label">
            {roll?.label || (isIt ? "Prova" : "Check")}
          </h2>
        </div>

        {/* Threshold + odds */}
        <div className="flex items-center justify-around text-center mb-6 px-2">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#94a3b8]">
              {STR_T.threshold_prefix}
            </div>
            <div className="font-heading text-2xl text-[#94a3b8] mt-1">
              ≥ {threshold}
              <span className="text-[#475569] text-sm">/100</span>
            </div>
          </div>
          <div className="w-px h-10 bg-[#475569]/40" />
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#94a3b8]">
              {STR_T.success_chance}
            </div>
            <div className="font-heading text-2xl text-emerald-300 mt-1" data-testid="dice-roll-success-pct">
              {successPct}%
            </div>
          </div>
        </div>

        {/* The Dice (two 3D cubes — tens + units) */}
        <div className="flex flex-col items-center justify-center mb-6">
          <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] mb-3">
            {STR_T.your_roll}
          </div>
          <div className="flex items-center justify-center gap-3" data-testid="dice-cubes">
            <DiceCube
              value={targetTens}
              spinning={isSpinning}
              ariaLabel="tens-die"
            />
            <DiceCube
              value={targetUnits}
              spinning={isSpinning}
              ariaLabel="units-die"
            />
          </div>
          {/* Total numeric readout — appears when dice settle */}
          <div
            className="mt-3 h-7 transition-opacity duration-300"
            style={{ opacity: phase !== "spinning" ? 1 : 0 }}
            data-testid="dice-total"
          >
            <span
              className="font-heading text-xl tabular-nums"
              style={{ color: isSuccess ? "#facc15" : "#f87171" }}
            >
              {percentile !== undefined ? percentile : "—"}
              <span className="text-[#475569] text-sm">/100</span>
            </span>
          </div>
          {/* Status hint */}
          <div className="mt-1 min-h-[40px] text-center text-[11px] uppercase tracking-wider flex items-center justify-center">
            {isSpinning && !tookTooLong && (
              <span className="flex items-center gap-1 text-[#94a3b8]">
                <Loader2 size={12} className="animate-spin" /> {STR_T.rolling}
              </span>
            )}
            {isSpinning && tookTooLong && (
              <div className="text-amber-400 font-body normal-case leading-snug max-w-[280px]">
                <p className="font-semibold">{isIt ? "Il destino sta tardando a rispondere..." : "Fate is taking longer than usual..."}</p>
                <p className="text-[10px] text-[#94a3b8] mt-0.5">
                  {isIt ? "La pergamena potrebbe essere già stata scritta." : "The scroll of destiny might have already been written."}
                </p>
              </div>
            )}
            {!isSpinning && (
              <span className={isSuccess ? "text-emerald-300" : "text-red-400"}>
                {isSuccess ? STR_T.landed_hi : STR_T.landed_lo}
              </span>
            )}
          </div>

          {/* Force close button when timeout occurs */}
          {isSpinning && tookTooLong && (
            <Button
              onClick={onClose}
              variant="outline"
              className="mt-3 border-amber-500/30 text-amber-300 hover:bg-amber-500/10 hover:border-amber-500/50 hover:text-amber-200 font-heading text-xs tracking-wider transition-all"
              data-testid="dice-roll-force-close"
            >
              {isIt ? "Verifica Stato Partita" : "Check Game Status"}
            </Button>
          )}
        </div>

        {/* Result banner — reveals after 600ms */}
        <div
          className={`text-center mb-5 transition-all duration-500 ${
            phase === "revealed" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
          }`}
          aria-live="polite"
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-heading text-sm tracking-widest"
            style={{
              borderColor: resultBoxColor,
              color: resultBoxColor,
              background: `${resultBoxColor}15`,
              boxShadow: `0 0 24px ${resultBoxColor}55`,
            }}
            data-testid={isSuccess ? "dice-roll-success" : "dice-roll-failure"}
          >
            {isSuccess ? <Sparkles size={16} /> : <Skull size={16} />}
            {isSuccess ? STR_T.success : STR_T.failure}
          </div>
        </div>

        {/* Continue button — disabled until reveal finished */}
        <div className="flex justify-center">
          <Button
            onClick={onClose}
            disabled={phase !== "revealed"}
            data-testid="dice-roll-continue"
            className={`min-w-[160px] font-heading text-sm tracking-wider transition-all ${
              isSuccess
                ? "bg-[#cfb53b] text-[#0a0a0c] hover:bg-[#e5cc5b]"
                : "bg-[#475569] text-white hover:bg-[#64748b]"
            } disabled:opacity-40`}
          >
            {STR_T.continue} <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
